import { useEffect, useState } from "react";
import api, { apiError, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, Send, Eye, MousePointerClick, Users } from "lucide-react";
import { toast } from "sonner";

const empty = { nombre: "", asunto: "", html: "<h2>Hola [Nombre_Cliente]</h2>\n<p>Novedades de West KAC...</p>", segment_estatus: "" };

export default function Campaigns() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/campaigns").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try { await api.post("/campaigns", { ...form, segment_estatus: form.segment_estatus || null }); toast.success("Campaña creada"); setOpen(false); setForm(empty); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const send = async (id) => { const { data } = await api.post(`/campaigns/${id}/send`); toast.success(`Enviada a ${data.sent} destinatarios`); load(); };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="text-3xl font-heading font-bold tracking-tight">E-mail Marketing</h1><p className="text-muted-foreground text-sm">Campañas segmentadas con métricas de apertura y clics.</p></div>
        <Button onClick={() => { setForm(empty); setOpen(true); }} data-testid="new-campaign-btn"><Plus className="w-4 h-4 mr-2" /> Nueva campaña</Button>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map((c) => (
          <Card key={c.id} className="p-5 rounded-lg" data-testid="campaign-card">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-heading font-semibold">{c.nombre}</h3>
              <Badge variant={c.estatus === "enviada" ? "default" : "secondary"}>{c.estatus}</Badge>
            </div>
            <p className="text-sm text-muted-foreground mb-4 line-clamp-1">{c.asunto}</p>
            <div className="grid grid-cols-3 gap-2 text-center text-sm mb-4">
              <div><div className="flex items-center justify-center gap-1 text-muted-foreground text-xs"><Users className="w-3 h-3" /> Env.</div><div className="font-semibold">{c.stats?.sent || 0}</div></div>
              <div><div className="flex items-center justify-center gap-1 text-muted-foreground text-xs"><Eye className="w-3 h-3" /> Aper.</div><div className="font-semibold">{c.stats?.opens || 0}</div></div>
              <div><div className="flex items-center justify-center gap-1 text-muted-foreground text-xs"><MousePointerClick className="w-3 h-3" /> Clics</div><div className="font-semibold">{c.stats?.clicks || 0}</div></div>
            </div>
            {c.estatus !== "enviada" && <Button size="sm" className="w-full" onClick={() => send(c.id)} data-testid="send-campaign"><Send className="w-4 h-4 mr-2" /> Enviar ahora</Button>}
          </Card>
        ))}
        {rows.length === 0 && <p className="text-muted-foreground col-span-full text-center py-8">Sin campañas aún.</p>}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Nueva campaña</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Nombre interno</Label><Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="campaign-nombre" /></div>
            <div><Label>Asunto del correo</Label><Input value={form.asunto} onChange={(e) => setForm({ ...form, asunto: e.target.value })} data-testid="campaign-asunto" /></div>
            <div><Label>Segmento (estatus del cliente)</Label>
              <Select value={form.segment_estatus} onValueChange={(v) => setForm({ ...form, segment_estatus: v })}>
                <SelectTrigger><SelectValue placeholder="Todos los clientes" /></SelectTrigger>
                <SelectContent><SelectItem value="activo">Activos</SelectItem><SelectItem value="prospecto">Prospectos</SelectItem><SelectItem value="inactivo">Inactivos</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Contenido HTML (usa [Nombre_Cliente])</Label><Textarea rows={6} value={form.html} onChange={(e) => setForm({ ...form, html: e.target.value })} className="font-mono text-xs" data-testid="campaign-html" /></div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="save-campaign">Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
